// ═══════════════════════════════════════════════════════════
// ADMIN ANALYTICS PAGE — Real Supabase data
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { AnalyticsPeriod, RadioAnalytics, AnalyticsRadioContext } from '@/types/analytics';
import { ANALYTICS_PERIOD_LABELS, ANALYTICS_PERIOD_PRESETS } from '@/types/analytics';
import { getRadioAnalytics, getMyAnalyticsRadios } from '@/services/analytics';
import KpiCard from './components/KpiCard';
import type { RealKpiData } from './components/KpiCard';
import TopsSection from './components/TopsSection';
import CommunityAnalyticsTabContent from './components/CommunityAnalyticsTabContent';
import RevenueTabContent from './components/RevenueTabContent';

type LoadState = 'loading' | 'ready' | 'empty' | 'error' | 'no_radio';
type AnalyticsView = 'audience' | 'community' | 'revenue';

const viewConfig: { key: AnalyticsView; label: string; icon: string; mobileLabel: string }[] = [
  { key: 'audience', label: 'Audiencia', icon: 'ri-bar-chart-grouped-line', mobileLabel: 'Audiencia' },
  { key: 'community', label: 'Comunidad', icon: 'ri-group-line', mobileLabel: 'Comunidad' },
  { key: 'revenue', label: 'Revenue', icon: 'ri-money-dollar-circle-line', mobileLabel: 'Revenue' },
];

function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0 min';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes} min`;
}

function buildKpis(analytics: RadioAnalytics): RealKpiData[] {
  const m = analytics.metrics;
  const f = analytics.followers;
  const e = analytics.engagement;
  const c = analytics.content;
  const s = analytics.stations;

  return [
    {
      id: 'oyentes', label: 'Oyentes totales', icon: 'ri-headphone-line', color: 'emerald',
      value: m.totalListeners, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'sesiones', label: 'Sesiones', icon: 'ri-bar-chart-line', color: 'sky',
      value: m.totalSessions, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'tiempo', label: 'Tiempo escuchado', icon: 'ri-timer-line', color: 'violet',
      value: Math.round(m.totalListeningSeconds / 60), unit: 'min', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'pico', label: 'Pico de oyentes', icon: 'ri-line-chart-line', color: 'amber',
      value: m.peakConcurrent, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'duracion', label: 'Duración promedio', icon: 'ri-time-line', color: 'orange',
      value: m.avgSessionSeconds, unit: 'seg', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'seguidores', label: 'Seguidores', icon: 'ri-user-heart-line', color: 'rose',
      value: f.totalFollowers, unit: '', trend: 'flat', trendLabel: `+${f.newFollowers}`,
    },
    {
      id: 'mensajes', label: 'Mensajes de chat', icon: 'ri-chat-3-line', color: 'indigo',
      value: e.totalChatMessages, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'solicitudes', label: 'Canciones solicitadas', icon: 'ri-music-2-line', color: 'teal',
      value: e.totalSongRequests, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'saludos', label: 'Saludos y dedicatorias', icon: 'ri-hand-heart-line', color: 'rose',
      value: e.totalGreetings, unit: '', trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'programas', label: 'Programas activos', icon: 'ri-calendar-2-line', color: 'sky',
      value: c.activePrograms, unit: `de ${c.totalPrograms}`, trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'podcasts', label: 'Podcasts', icon: 'ri-headphone-line', color: 'violet',
      value: c.totalPodcasts, unit: `${c.totalEpisodes} episodios`, trend: 'flat', trendLabel: '0%',
    },
    {
      id: 'estaciones', label: 'Estaciones activas', icon: 'ri-broadcast-line', color: 'emerald',
      value: s.activeStations, unit: `de ${s.totalStations}`, trend: 'flat', trendLabel: `${s.streamAvailability}% disp.`,
    },
  ];
}

export default function AdminAnalyticsPage() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [analytics, setAnalytics] = useState<RadioAnalytics | null>(null);
  const [period, setPeriod] = useState<AnalyticsPeriod>('LAST_7_DAYS');
  const [analyticsView, setAnalyticsView] = useState<AnalyticsView>('audience');
  const [radios, setRadios] = useState<AnalyticsRadioContext[]>([]);
  const [selectedRadioId, setSelectedRadioId] = useState<string>('');
  const [kpis, setKpis] = useState<RealKpiData[]>([]);

  // Cargar lista de radios del usuario
  useEffect(() => {
    let cancelled = false;
    getMyAnalyticsRadios().then((r) => {
      if (cancelled) return;
      setRadios(r);
      if (r.length === 0) {
        setDataState('no_radio');
      } else if (!selectedRadioId) {
        setSelectedRadioId(r[0].radioId);
      }
    }).catch(() => {
      if (!cancelled) setDataState('error');
    });
    return () => { cancelled = true; };
  }, []);

  // Cargar analytics cuando cambia radio o período
  const loadAnalytics = useCallback(async (radioId: string, p: AnalyticsPeriod) => {
    if (!radioId) return;
    setDataState('loading');
    try {
      const radio = radios.find((r) => r.radioId === radioId);
      const result = await getRadioAnalytics(radioId, radio?.radioName || 'Radio', p);
      setAnalytics(result);

      // Determinar si hay datos (todas las métricas en cero)
      const hasData =
        result.metrics.totalListeners > 0 ||
        result.metrics.totalSessions > 0 ||
        result.metrics.totalListeningSeconds > 0 ||
        result.followers.totalFollowers > 0 ||
        result.engagement.totalChatMessages > 0 ||
        result.content.totalPrograms > 0 ||
        result.content.totalPodcasts > 0;

      if (hasData) {
        setDataState('ready');
      } else {
        setDataState('empty');
      }
      setKpis(buildKpis(result));
    } catch {
      setDataState('error');
      setAnalytics(null);
    }
  }, [radios]);

  useEffect(() => {
    if (selectedRadioId) {
      loadAnalytics(selectedRadioId, period);
    }
  }, [selectedRadioId, period, loadAnalytics]);

  const selectedRadio = radios.find((r) => r.radioId === selectedRadioId);
  const periodLabel = ANALYTICS_PERIOD_LABELS[period];

  /* ═══════ NO RADIO ═══════ */
  if (dataState === 'no_radio') {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center">
        <div className="text-center max-w-sm mx-auto px-4 py-24">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-radio-line text-3xl text-foreground-400"></i>
          </div>
          <h3 className="text-foreground-950 font-bold text-base mb-2">Sin radios para analizar</h3>
          <p className="text-foreground-500 text-sm">No administrás ninguna radio. Registrá una para ver sus estadísticas.</p>
        </div>
      </div>
    );
  }

  /* ═══════ LOADING ═══════ */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
              <div className="w-24 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
          </div>
        </header>
        <div className="flex">
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-56 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-6 animate-pulse">
                <div className="w-48 h-5 rounded-md bg-background-200 mb-3"></div>
                <div className="w-72 h-3 rounded bg-background-200"></div>
                <div className="flex gap-2 mt-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="w-20 h-9 rounded-full bg-background-200"></div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-4 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-16 h-3 rounded bg-background-200"></div>
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                    </div>
                    <div className="w-20 h-7 rounded-md bg-background-200 mb-2"></div>
                    <div className="w-24 h-3 rounded bg-background-200"></div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ═══════ ERROR ═══════ */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center">
        <div className="text-center max-w-sm mx-auto px-4 py-24">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar estadísticas</h3>
          <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar los datos. Intentá de nuevo.</p>
          <button
            onClick={() => selectedRadioId && loadAnalytics(selectedRadioId, period)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ═══════ EMPTY / READY (comparten UI, difieren en datos) ═══════ */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${analyticsView === 'audience' ? 'bg-accent-500' : analyticsView === 'community' ? 'bg-secondary-500' : 'bg-amber-500'}`}>
              <i className={`${analyticsView === 'audience' ? 'ri-bar-chart-grouped-line' : analyticsView === 'community' ? 'ri-group-line' : 'ri-money-dollar-circle-line'} text-background-50 text-xs`}></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Analytics</h1>
              <p className="text-foreground-400 text-[9px]">
                {selectedRadio?.radioName || ''} · {periodLabel}
              </p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">
        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">
                {analyticsView === 'audience' ? 'Analytics de Audiencia' : analyticsView === 'community' ? 'Analytics de Comunidad' : 'Analytics de Revenue'}
              </h2>
              <p className="text-foreground-400 text-xs">
                {analyticsView === 'audience'
                  ? `${selectedRadio?.radioName || 'Radio'} · ${periodLabel}`
                  : analyticsView === 'community'
                    ? 'Interacción y engagement de tu audiencia'
                    : 'Ingresos y rendimiento publicitario'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* View switcher */}
              <div className="flex items-center bg-background-100 rounded-full p-1">
                {viewConfig.map((view) => (
                  <button
                    key={view.key}
                    onClick={() => setAnalyticsView(view.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      analyticsView === view.key
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-400 hover:text-foreground-600'
                    }`}
                  >
                    <i className={`${view.icon} text-xs`}></i>
                    {view.label}
                  </button>
                ))}
              </div>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Mobile view switcher */}
          <div className="lg:hidden px-4 pt-4 pb-2">
            <div className="flex items-center bg-background-100 rounded-full p-1">
              {viewConfig.map((view) => (
                <button
                  key={view.key}
                  onClick={() => setAnalyticsView(view.key)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap flex-1 justify-center ${
                    analyticsView === view.key
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className={`${view.icon} text-[10px]`}></i>
                  {view.mobileLabel}
                </button>
              ))}
            </div>
          </div>

          {analyticsView === 'audience' ? (
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
              {/* Hero + Controls */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-bar-chart-grouped-line text-xl text-accent-500"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-950 font-extrabold text-lg">Estadísticas de tu Radio</h3>
                      <p className="text-foreground-400 text-xs mt-0.5">
                        Datos del período <strong className="text-foreground-700">{periodLabel.toLowerCase()}</strong>
                        {dataState === 'empty' && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[10px] font-medium">
                            Sin datos aún
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Radio selector */}
                  {radios.length > 1 && (
                    <select
                      value={selectedRadioId}
                      onChange={(e) => setSelectedRadioId(e.target.value)}
                      className="px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-medium cursor-pointer outline-none focus:border-primary-300 transition-colors"
                    >
                      {radios.map((r) => (
                        <option key={r.radioId} value={r.radioId}>{r.radioName}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Period selector */}
                <div className="flex items-center bg-background-100 rounded-full p-1 overflow-x-auto">
                  {ANALYTICS_PERIOD_PRESETS.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                        period === p
                          ? 'bg-background-50 text-foreground-950 shadow-sm'
                          : 'text-foreground-400 hover:text-foreground-600'
                      }`}
                    >
                      {ANALYTICS_PERIOD_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Empty state banner (visible pero con KPIs en cero) */}
              {dataState === 'empty' && (
                <div className="bg-foreground-50 border border-foreground-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-foreground-100 flex items-center justify-center shrink-0">
                      <i className="ri-bar-chart-line text-foreground-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-foreground-700 text-xs font-semibold">Sin datos estadísticos</p>
                      <p className="text-foreground-400 text-[10px]">
                        Todavía no hay actividad registrada para {selectedRadio?.radioName || 'esta radio'} en el período seleccionado.
                        Las métricas aparecerán cuando los oyentes empiecen a interactuar.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* KPI Cards Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {kpis.map((kpi) => (
                  <KpiCard key={kpi.id} kpi={kpi} />
                ))}
              </div>

              {/* Tops Section */}
              {analytics && (
                <TopsSection
                  topPrograms={analytics.topPrograms}
                  topPodcasts={analytics.topPodcasts}
                />
              )}

              {/* Footer badge */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                      <i className="ri-database-2-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-accent-800 text-xs font-semibold">Datos desde Supabase</p>
                      <p className="text-accent-600/80 text-[10px]">
                        Métricas agregadas por el backend. Los datos se actualizan diariamente.
                        {dataState === 'empty' && ' Sin registros aún para este período.'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap flex items-center gap-1 w-fit ${
                    dataState === 'empty' ? 'bg-foreground-100 text-foreground-500' : 'bg-accent-200 text-accent-700'
                  }`}>
                    <i className="ri-time-line text-[9px]"></i>
                    {dataState === 'empty' ? 'Sin datos' : 'Datos reales'}
                  </span>
                </div>
              </div>
            </div>
          ) : analyticsView === 'community' ? (
            <div className="p-4 md:p-6 lg:p-8 pb-8">
              <CommunityAnalyticsTabContent />
            </div>
          ) : (
            <div className="p-4 md:p-6 lg:p-8 pb-8">
              <RevenueTabContent />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}